import { useMemo, useState, type FunctionComponent } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@heroicons/react/24/solid/XMarkIcon';
import MagnifyingGlassIcon from '@heroicons/react/24/solid/MagnifyingGlassIcon';
import { format } from 'date-fns';
import { DATE_TIME_FORMAT, ASSISTANT_API } from '@starjack/utils';
import {
    CHAT_PAGE_SIZE_DEFAULT,
    useGetAssistantChatQuery,
    useGetAssistantChatsQuery,
    type ChatListItem,
    type ChatMessageRecord,
    type ChatOrigin,
} from '@starjack/entities/assistantConfig';
import {
    StarjackTable,
    type StajackTableColumn,
} from '@/components/Table/v2/Table';
import {
    isConnectionError,
    MISCONFIGURED_MESSAGE,
} from '@/pageContents/kiEinstellungen/assistantApiError';

const formatDateTime = (iso: string): string => {
    try {
        return format(new Date(iso), DATE_TIME_FORMAT);
    } catch {
        return iso;
    }
};

const originLabel = (origin: ChatOrigin): string =>
    origin.kind === 'portal' ? 'Portal' : `Webshop (${origin.liftOperatorId})`;

/**
 * Every message in `seq` order, user right / assistant left. `role` is a
 * free-text column in the store (see chat-monitoring service), so anything
 * that isn't literally `'user'` renders on the assistant side rather than
 * silently dropping an unrecognised role.
 */
const TranscriptMessage: FunctionComponent<{
    role: string;
    content: string;
    createdAt: string;
}> = ({ role, content, createdAt }) => {
    const isUser = role === 'user';
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
            }}
        >
            <Paper
                variant='outlined'
                sx={{
                    p: 1.5,
                    maxWidth: '70%',
                    bgcolor: isUser ? 'action.selected' : 'background.paper',
                }}
            >
                <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mb: 0.5 }}
                >
                    {role} · {formatDateTime(createdAt)}
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                    {content}
                </Typography>
            </Paper>
        </Box>
    );
};

/**
 * Filters client-side on the already-loaded transcript rather than asking the
 * server: the whole chat is in hand, and a search endpoint would write a
 * second PII access-log line per keystroke (see `getAssistantChat`).
 */
const matchesSearch = (message: ChatMessageRecord, needle: string): boolean => {
    if (needle === '') return true;
    return message.content.toLocaleLowerCase().includes(needle);
};

const TranscriptDialog: FunctionComponent<{
    chatId: string | null;
    onClose: () => void;
}> = ({ chatId, onClose }) => {
    // `skipToken` — not `skip: chatId === null` with a fallback string arg —
    // is what makes "no chat selected" a state the query genuinely never
    // runs for, rather than a request that fires with a placeholder id.
    const { data, isLoading, error } = useGetAssistantChatQuery(
        chatId ?? skipToken
    );
    const [search, setSearch] = useState('');

    const needle = search.trim().toLocaleLowerCase();
    const messages = useMemo(() => {
        if (!data) return [];
        return [...data.messages]
            .sort((a, b) => a.seq - b.seq)
            .filter((message) => matchesSearch(message, needle));
    }, [data, needle]);

    const close = () => {
        // The search term belongs to the chat that was open, not to the next
        // one — a stale filter would make the next transcript look truncated.
        setSearch('');
        onClose();
    };

    return (
        <Dialog
            open={chatId !== null}
            onClose={close}
            fullWidth
            maxWidth='lg'
            // A transcript is as long as the conversation was; a paper sized to
            // its content jumps between a three-line chat and a hundred-turn
            // one. Fixed full height, and the message list scrolls inside it.
            slotProps={{ paper: { sx: { height: '100%' } } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Typography variant='h6'>
                        {data?.title ?? 'Chat'}
                    </Typography>
                    {data && (
                        <Typography variant='body2' color='text.secondary'>
                            {data.user ?? 'Anonym'} · {originLabel(data.origin)}{' '}
                            · {data.locale ?? '-'} · {data.messageCount}{' '}
                            Nachrichten
                            {data.feedbackScore !== null &&
                                ` · Bewertung ${data.feedbackScore}`}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={close} size='small' aria-label='Schließen'>
                    <SvgIcon fontSize='small'>
                        <CloseIcon />
                    </SvgIcon>
                </IconButton>
            </DialogTitle>
            {/* `overflow: hidden` on the content and `overflowY: auto` on the
                message list below is what keeps the warning and the search
                field pinned while only the transcript scrolls. */}
            <DialogContent
                dividers
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Alert severity='warning' sx={{ mb: 2 }}>
                    Chatverläufe können personenbezogene Daten enthalten.
                </Alert>

                {isLoading && <CircularProgress />}
                {error && (
                    <Alert severity='error'>
                        {isConnectionError(error)
                            ? 'Assistant nicht erreichbar. Läuft der Dienst, und ist VITE_ASSISTANT_API_URL gesetzt?'
                            : 'Chat nicht ladbar.'}
                    </Alert>
                )}

                {data && (
                    <>
                        {(data.feedbackComment || data.bugReportCount > 0) && (
                            <Alert severity='info' sx={{ mb: 2 }}>
                                {data.feedbackComment && (
                                    <Typography variant='body2'>
                                        Feedback-Kommentar:{' '}
                                        {data.feedbackComment}
                                    </Typography>
                                )}
                                {data.bugReportCount > 0 && (
                                    <Typography variant='body2'>
                                        Fehlermeldungen: {data.bugReportCount}
                                    </Typography>
                                )}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            variant='filled'
                            size='small'
                            label='Im Chatverlauf suchen'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SvgIcon fontSize='small'>
                                                <MagnifyingGlassIcon />
                                            </SvgIcon>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            helperText={
                                needle === ''
                                    ? undefined
                                    : `${messages.length} von ${
                                          data.messages.length
                                      } Nachrichten enthalten „${search.trim()}“.`
                            }
                            sx={{ mb: 2 }}
                        />

                        <Stack
                            spacing={1.5}
                            sx={{ flex: 1, overflowY: 'auto', pr: 1, mt: 4 }}
                        >
                            {messages.length === 0 && needle !== '' && (
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                >
                                    Keine Nachricht enthält diesen Suchbegriff.
                                </Typography>
                            )}
                            {messages.map((message) => (
                                <TranscriptMessage
                                    key={message.id}
                                    role={message.role}
                                    content={message.content}
                                    createdAt={message.createdAt}
                                />
                            ))}
                        </Stack>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export const AssistantChatsContent: FunctionComponent = () => {
    // Offset pagination straight through to the server: `page` and
    // `rowsPerPage` are the query args, so there is no accumulated client-side
    // list to keep in sync. `usePagination` is deliberately NOT used here (it
    // slices an array the caller already holds in full) — chats grow without
    // bound, so the page the server sends is the page the table shows.
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(CHAT_PAGE_SIZE_DEFAULT);
    const [selectedChatId, setSelectedChatId] = useState<string | null>(null);

    const { data, isFetching, error, refetch } = useGetAssistantChatsQuery(
        { page, limit: rowsPerPage },
        { skip: !ASSISTANT_API }
    );

    if (!ASSISTANT_API)
        return <Alert severity='error'>{MISCONFIGURED_MESSAGE}</Alert>;

    const columns: StajackTableColumn<ChatListItem>[] = [
        { label: 'Chat-ID', itemKey: 'id' },
        { label: 'Kunde', itemTransform: (row) => row.user ?? 'Anonym' },
        { label: 'Herkunft', itemTransform: (row) => originLabel(row.origin) },
        { label: 'Sprache', itemTransform: (row) => row.locale ?? '-' },
        {
            label: 'Nachrichten',
            itemKey: 'messageCount',
            options: {
                headerCellProps: { align: 'right' },
                rowCellProps: { align: 'right' },
            },
        },
        {
            label: 'Bewertung',
            itemTransform: (row) => row.feedbackScore ?? '-',
            options: {
                headerCellProps: { align: 'right' },
                rowCellProps: { align: 'right' },
            },
        },
        {
            label: 'Beginn',
            itemTransform: (row) => formatDateTime(row.createdAt),
        },
    ];

    // A read error keeps the table mounted rather than replacing it: the rows
    // on screen are still the last thing the server confirmed, and blanking
    // them on a transient failure loses support's place mid-call.
    const items = data?.items ?? [];
    const total = data?.total ?? 0;

    // Reads fail open (an unreadable table renders as an empty page, not an
    // error — see the service), so a manual reload is the only way support has
    // to rule out "the read silently failed" during a live call.
    const rowsPerPageChanged = (next: number) => {
        setRowsPerPage(next);
        setPage(0);
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    onClick={() => void refetch()}
                    disabled={isFetching}
                    variant='contained'
                >
                    Neu laden
                </Button>
            </Box>

            {error && (
                <Alert severity='error' sx={{ mb: 2 }}>
                    {isConnectionError(error)
                        ? 'Assistant nicht erreichbar.'
                        : 'Chatverläufe nicht ladbar'}
                </Alert>
            )}

            {!error && !isFetching && total === 0 && (
                <Alert severity='info' sx={{ mb: 2 }}>
                    Keine Chatverläufe gefunden. Ein Lesefehler auf dem Server
                    würde hier ebenso als leere Liste erscheinen - bei Verdacht
                    auf einen Fehler bitte &quot;Neu laden&quot; verwenden oder
                    die Server-Logs prüfen.
                </Alert>
            )}

            <StarjackTable<ChatListItem>
                count={total}
                items={items}
                currentPage={page}
                onPageChange={setPage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={rowsPerPageChanged}
                loading={isFetching}
                error={error}
                itemId='id'
                columns={columns}
                onRowClick={(row) => setSelectedChatId(row.id)}
                noDataLabel='Keine Chatverläufe gefunden'
            />

            <TranscriptDialog
                chatId={selectedChatId}
                onClose={() => setSelectedChatId(null)}
            />
        </>
    );
};
